(() => {
  if (window.FXBGContourEditor) return;
  class ContourEditor {
    constructor(host) {
      this.host = host;
      this.preview = host.querySelector('.fxbg-sticker__preview');
      this.version = 0;
      this.canvas = document.createElement('canvas');
      this.canvas.width = 720;
      this.canvas.height = 560;
      this.canvas.hidden = true;
      this.canvas.className = 'fxbg-contour-canvas';
      this.canvas.setAttribute('aria-label', 'Artwork with white border and magenta cut line');
      this.preview.append(this.canvas);
      this.caption = document.createElement('p');
      this.caption.className = 'fxbg-proof-caption';
      this.caption.textContent = 'Online proof - 1 pt magenta cut line';
      this.caption.hidden = true;
      this.preview.insertAdjacentElement('afterend', this.caption);
      this.panel = document.createElement('div');
      this.panel.className = 'fxbg-contour-controls';
      this.panel.hidden = true;
      this.panel.innerHTML = `<div data-page-controls hidden><label>Artwork page <select data-artwork-page aria-label="Artwork page"></select></label></div>
        <label>Border padding <select data-border aria-label="Border padding">
        <option value="0.0625">1/16 inch</option><option value="0.125" selected>1/8 inch</option>
        <option value="0.1875">3/16 inch</option><option value="0.25">1/4 inch</option>
        </select></label><label><input data-cut type="checkbox" checked> Show cut line</label>
        <label><input data-bleed type="checkbox"> Extend background past cut line</label>
        <div class="fxbg-bleed-options" data-bleed-options hidden>
          <label>Bleed background color <input data-bleed-color type="color" value="#ffffff" aria-label="Bleed background color"></label>
          <p>Color extends 1/8 inch beyond the cut. Choose the background color to match your artwork.</p>
        </div>
        <label><input data-background type="checkbox"> Remove flat outer background</label>
        <p>1 pt magenta line marks the cut. Padding is included in the finished size.</p>`;
      const upload = host.querySelector('[data-upload-zone]');
      (upload || this.preview).insertAdjacentElement('afterend', this.panel);
      this.status = document.createElement('p');
      this.status.className = 'fxbg-contour-status';
      this.status.setAttribute('role', 'status');
      this.panel.insertAdjacentElement('afterend', this.status);
      const form = host.querySelector('form');
      this.property = document.createElement('input');
      this.property.type = 'hidden';
      this.property.name = 'properties[Border padding]';
      this.property.disabled = true;
      form?.append(this.property);
      this.cutProperty = document.createElement('input');
      this.cutProperty.type = 'hidden';
      this.cutProperty.name = 'properties[Cut line]';
      this.cutProperty.value = '1 pt magenta';
      this.cutProperty.disabled = true;
      form?.append(this.cutProperty);
      this.bleedProperty = document.createElement('input');
      this.bleedProperty.type = 'hidden';
      this.bleedProperty.name = 'properties[Background bleed]';
      this.bleedProperty.disabled = true;
      form?.append(this.bleedProperty);
      this.bleedColorProperty = document.createElement('input');
      this.bleedColorProperty.type = 'hidden';
      this.bleedColorProperty.name = 'properties[Bleed color]';
      this.bleedColorProperty.disabled = true;
      form?.append(this.bleedColorProperty);
      this.pageProperty = document.createElement('input');
      this.pageProperty.type = 'hidden';
      this.pageProperty.name = 'properties[Artwork page]';
      this.pageProperty.disabled = true;
      form?.append(this.pageProperty);
      this.bleedColorCustomized = false;
      this.events = new AbortController();
      let scheduled = false;
      this.updateStickyTop = () => {
        const header = document.querySelector('.section-header, .shopify-section-header');
        const hidden = header?.classList.contains('shopify-section-header-hidden');
        const bottom = header && !hidden ? Math.max(0, header.getBoundingClientRect().bottom) : 0;
        host.style.setProperty('--fxbg-proof-top', `${Math.ceil(bottom) + 12}px`);
      };
      const schedule = () => {
        if (scheduled) return;
        scheduled = true;
        requestAnimationFrame(() => { scheduled = false; this.updateStickyTop(); });
      };
      window.addEventListener('scroll', schedule, { passive: true, signal: this.events.signal });
      window.addEventListener('resize', schedule, { signal: this.events.signal });
      const header = document.querySelector('.section-header, .shopify-section-header');
      if (header) {
        this.headerObserver = new MutationObserver(schedule);
        this.headerObserver.observe(header, { attributes: true, attributeFilter: ['class', 'style'] });
        header.addEventListener('transitionend', schedule, { signal: this.events.signal });
      }
      this.updateStickyTop();
      this.panel.addEventListener('change', (event) => {
        if (event.target.matches('[data-artwork-page]')) this.changePage(Number(event.target.value));
        else this.render();
      });
      this.panel.querySelector('[data-bleed-color]').addEventListener('input', () => {
        this.bleedColorCustomized = true;
        this.render();
      });
      this.onSizeChange = (event) => {
        if (event.target.name === 'sticker_size' && this.source) this.render();
      };
      host.addEventListener('change', this.onSizeChange);
    }

    restore() {
      this.source = null;
      this.canvas.getContext('2d').clearRect(0, 0, 720, 560);
      this.canvas.hidden = true;
      this.panel.hidden = true;
      this.caption.hidden = true;
      this.property.disabled = true;
      this.cutProperty.disabled = true;
      this.bleedProperty.disabled = true;
      this.bleedColorProperty.disabled = true;
      this.pageProperty.disabled = true;
      this.panel.querySelector('[data-page-controls]').hidden = true;
      this.host.classList.remove('fxbg-proof-active');
      this.preview.querySelectorAll('.fxbg-sticker__image').forEach(image => { image.hidden = false; });
    }

    async setFile(file) {
      const version = ++this.version;
      this.artworkDocument?.destroy();
      this.artworkDocument = null;
      this.restore();
      this.status.textContent = '';
      if (!file) return;
      this.status.textContent = 'Preparing contour preview...';
      try {
        if (!window.FXBGArtworkLoader) throw new Error('Artwork preview assets are missing. Please refresh the page.');
        const doc = await window.FXBGArtworkLoader.open(file, this.host.dataset);
        if (version !== this.version) { doc.destroy(); return; }
        this.artworkDocument = doc;
        const select = this.panel.querySelector('[data-artwork-page]');
        select.replaceChildren();
        for (let number=1; number<=doc.pages; number++) {
          const option = document.createElement('option');
          option.value = String(number);
          option.textContent = `Page ${number} of ${doc.pages}`;
          select.append(option);
        }
        await this.showPage(doc,1,version);
      } catch (error) {
        if (version !== this.version) return;
        this.artworkDocument?.destroy();
        this.artworkDocument = null;
        this.restore();
        this.status.textContent = error.message || 'Could not preview this file. Export a PDF or PNG and try again.';
      }
    }

    async showPage(doc, number, version) {
      const source = await doc.renderPage(number);
      if (version !== this.version || doc !== this.artworkDocument) return;
      this.source = source;
      this.currentPage = number;
      const select = this.panel.querySelector('[data-artwork-page]');
      select.value = String(number);
      select.disabled = false;
      this.panel.querySelector('[data-page-controls]').hidden = doc.pages <= 1;
      if (!this.bleedColorCustomized) {
        const prepared = this.prepareArtwork();
        if (prepared) this.panel.querySelector('[data-bleed-color]').value = this.sampleEdgeColor(prepared);
      }
      this.render();
    }

    async changePage(number) {
      const doc = this.artworkDocument;
      if (!doc || number<1 || number>doc.pages) return;
      const version = ++this.version;
      this.canvas.hidden = true;
      this.canvas.getContext('2d').clearRect(0,0,720,560);
      this.source = null;
      this.pageProperty.disabled = true;
      this.property.disabled = true;
      this.cutProperty.disabled = true;
      this.bleedProperty.disabled = true;
      this.bleedColorProperty.disabled = true;
      this.panel.querySelector('[data-artwork-page]').disabled = true;
      this.status.textContent = `Preparing page ${number}...`;
      try { await this.showPage(doc,number,version); }
      catch (error) {
        if (version !== this.version) return;
        this.panel.querySelector('[data-artwork-page]').disabled = false;
        this.status.textContent = 'Could not preview this page. Choose another page or upload a new file.';
      }
    }

    prepareArtwork() {
      const canvas = document.createElement('canvas');
      canvas.width = this.source.width;
      canvas.height = this.source.height;
      const ctx = canvas.getContext('2d', { willReadFrequently: true });
      ctx.drawImage(this.source, 0, 0);
      const image = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const { data } = image;
      const w = canvas.width, h = canvas.height;
      if (this.panel.querySelector('[data-background]').checked) {
        // Remove only background connected to a matching canvas edge.
        const seen = new Uint8Array(w * h), queue = new Int32Array(w * h);
        const seed = data.slice(0, 4);
        let head = 0, tail = 0;
        const add = (i) => {
          if (seen[i]) return;
          const p = i * 4;
          const matches = data[p + 3] < 20 || (seed[3] > 20 &&
            Math.abs(data[p] - seed[0]) + Math.abs(data[p + 1] - seed[1]) + Math.abs(data[p + 2] - seed[2]) < 96);
          if (matches) { seen[i] = 1; queue[tail++] = i; }
        };
        for (let x = 0; x < w; x++) { add(x); add((h - 1) * w + x); }
        for (let y = 0; y < h; y++) { add(y * w); add(y * w + w - 1); }
        while (head < tail) {
          const i = queue[head++], x = i % w;
          data[i * 4 + 3] = 0;
          if (x) add(i - 1);
          if (x < w - 1) add(i + 1);
          if (i >= w) add(i - w);
          if (i < w * (h - 1)) add(i + w);
        }
        ctx.putImageData(image, 0, 0);
      }
      let minX = w, minY = h, maxX = -1, maxY = -1;
      for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
        if (data[(y * w + x) * 4 + 3] <= 20) continue;
        minX = Math.min(minX, x); minY = Math.min(minY, y);
        maxX = Math.max(maxX, x); maxY = Math.max(maxY, y);
      }
      return maxX < 0 ? null : { canvas, x: minX, y: minY, w: maxX - minX + 1, h: maxY - minY + 1 };
    }

    sampleEdgeColor(art) {
      const w = art.canvas.width, h = art.canvas.height;
      const data = art.canvas.getContext('2d', { willReadFrequently: true }).getImageData(0, 0, w, h).data;
      const bins = new Map();
      const opaque = (x, y) => x >= 0 && y >= 0 && x < w && y < h && data[(y * w + x) * 4 + 3] >= 128;
      for (let y = art.y; y < art.y + art.h; y++) for (let x = art.x; x < art.x + art.w; x++) {
        if (!opaque(x, y) || (opaque(x-1,y) && opaque(x+1,y) && opaque(x,y-1) && opaque(x,y+1))) continue;
        const i = (y * w + x) * 4;
        const key = `${data[i] >> 5},${data[i+1] >> 5},${data[i+2] >> 5}`;
        const bin = bins.get(key) || [0,0,0,0];
        bin[0]++; bin[1] += data[i]; bin[2] += data[i+1]; bin[3] += data[i+2];
        bins.set(key, bin);
      }
      const best = [...bins.values()].sort((a,b) => b[0]-a[0])[0];
      return best ? '#' + best.slice(1).map(v => Math.round(v/best[0]).toString(16).padStart(2,'0')).join('') : '#ffffff';
    }

    paintProof(mask, layer, size, bleedEnabled, color) {
      const ctx = this.canvas.getContext('2d');
      ctx.clearRect(0, 0, 720, 560);
      if (bleedEnabled) {
        // Keep the finished cut geometry fixed; only expand the background underneath it.
        const bleed = 430 * 0.125 / size;
        window.FXBGContourCore.stampOutline(mask, color, bleed, ctx);
      } else {
        ctx.drawImage(mask, 0, 0);
      }
      ctx.drawImage(layer, 0, 0);
      const lineWidth = 430 / (size * 72);
      if (this.panel.querySelector('[data-cut]').checked) window.FXBGContourCore.strokeMaskContour(mask, ctx, lineWidth);
      this.canvas.dataset.cutLineWidth = String(lineWidth);
    }

    render() {
      if (!this.source) return;
      const core = window.FXBGContourCore;
      if (!core) { this.status.textContent = 'Contour preview is unavailable. Please refresh the page.'; return; }
      const art = this.prepareArtwork();
      if (!art) {
        this.canvas.hidden = true;
        this.caption.hidden = true;
        this.host.classList.remove('fxbg-proof-active');
        this.property.disabled = true;
        this.cutProperty.disabled = true;
        this.bleedProperty.disabled = true;
        this.bleedColorProperty.disabled = true;
        this.pageProperty.disabled = true;
        this.panel.hidden = false;
        this.status.textContent = 'No visible artwork remains. Turn off background removal or choose another image.';
        return;
      }
      const size = parseFloat(this.host.querySelector('input[name="sticker_size"]:checked')?.value) || 3;
      const border = Number(this.panel.querySelector('[data-border]').value);
      const padding = 430 * border / size;
      const scale = (430 - padding * 2) / Math.max(art.w, art.h);
      const w = art.w * scale, h = art.h * scale;
      const layer = document.createElement('canvas');
      layer.width = 720; layer.height = 560;
      layer.getContext('2d').drawImage(art.canvas, art.x, art.y, art.w, art.h, (720 - w) / 2, (560 - h) / 2, w, h);
      const mask = document.createElement('canvas');
      mask.width = 720; mask.height = 560;
      core.stampOutline(layer, '#fff', padding, mask.getContext('2d', { willReadFrequently: true }));
      core.fillContourHoles(mask);
      core.smoothAlphaMask(mask, 2);
      const bleedEnabled = this.panel.querySelector('[data-bleed]').checked;
      const bleedColor = this.panel.querySelector('[data-bleed-color]').value;
      this.panel.querySelector('[data-bleed-options]').hidden = !bleedEnabled;
      this.paintProof(mask, layer, size, bleedEnabled, bleedColor);
      this.preview.querySelectorAll('.fxbg-sticker__image').forEach(image => { image.hidden = true; });
      this.canvas.hidden = false;
      this.panel.hidden = false;
      this.caption.hidden = false;
      this.cutProperty.disabled = false;
      const wasActive = this.host.classList.contains('fxbg-proof-active');
      this.host.classList.add('fxbg-proof-active');
      if (!wasActive) this.updateStickyTop();
      this.property.value = `${border} inch`;
      this.property.disabled = false;
      this.bleedProperty.disabled = false;
      this.bleedProperty.value = bleedEnabled ? '1/8 inch beyond cut line' : 'Off';
      this.bleedColorProperty.disabled = !bleedEnabled;
      this.bleedColorProperty.value = bleedColor;
      const paged = this.artworkDocument && /PDF|AI/.test(this.artworkDocument.kind);
      this.pageProperty.disabled = !paged;
      this.pageProperty.value = paged ? `${this.currentPage} of ${this.artworkDocument.pages}` : '';
      this.caption.textContent = bleedEnabled ? 'Online proof - magenta cut line, color beyond line is bleed' : 'Online proof - 1 pt magenta cut line';
      this.status.textContent = (paged ? `Page ${this.currentPage} of ${this.artworkDocument.pages}. Original file retained. ` : '') + 'Preview only. Your final proof confirms the cut line and any separate artwork pieces.';
    }

    destroy() {
      ++this.version;
      this.artworkDocument?.destroy();
      this.host.removeEventListener('change', this.onSizeChange);
      this.events.abort();
      this.headerObserver?.disconnect();
      this.host.classList.remove('fxbg-proof-active');
      this.canvas.remove(); this.panel.remove(); this.status.remove(); this.property.remove();
      this.caption.remove(); this.cutProperty.remove();
      this.bleedProperty.remove(); this.bleedColorProperty.remove();
      this.pageProperty.remove();
    }
  }
  window.FXBGContourEditor = ContourEditor;
})();



